import {
  NirvanaErrorCode,
  NirvanaErrorCodeValues,
  NirvanaSanitizedError,
} from '@nirvana/shared/dtos/types/shared/communication';
import { v4 as uuidv4 } from 'uuid';

export class NirvanaError extends Error {
  public readonly id: string;
  public readonly name = NIRVANA_ERROR_NAME;
  public readonly code: NirvanaErrorCode;
  public readonly message: string;
  public readonly original: any;
  public readonly stack?: string;
  public readonly data?: any;

  constructor(error: any, code?: number, data?: any) {
    super();

    const parsedData = parseError(error);
    this.id = typeof parsedData.id === 'string' ? parsedData.id : uuidv4();
    this.message = typeof parsedData.message === 'string' ? parsedData.message : DEFAULT_ERROR_MESSAGE;
    this.original = error;

    const mergedCode = code !== undefined ? code : parsedData.code;
    if (NirvanaErrorCodeValues.includes(mergedCode)) {
      this.code = mergedCode;
    } else {
      this.code = NirvanaErrorCode.UNKNOWN;
    }

    if (data) {
      this.data = data;
    } else if (parsedData.data) {
      this.data = data;
    }

    if (typeof parsedData.stack === 'string') {
      this.stack = parsedData.stack;
    } else if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NirvanaError);
    }
  }
}

export class NirvanaErrorFeatureUnavailable extends NirvanaError {
  constructor(data?: any) {
    super(publicErrorMessages.get(NirvanaErrorCode.FEATURE_CURRENTLY_NOT_AVAILABLE), NirvanaErrorCode.FEATURE_CURRENTLY_NOT_AVAILABLE, data);
  }
}

function parseError(error: any) {
  if (error instanceof NirvanaError) {
    return error;
  }

  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }

  if (typeof error === 'object' && error != null && !Array.isArray(error)) {
    if (error.name === NIRVANA_ERROR_NAME) {
      return error;
    } else {
      return { message: error.message, stack: error.stack };
    }
  }

  return { message: error };
}

export function fromError(error?: any) {
  if (error instanceof NirvanaError) {
    return error;
  }

  return new NirvanaError(error);
}

export function sanitizeError(error?: any): NirvanaSanitizedError {
  const nirvanaError = fromError(error);
  return {
    id: nirvanaError.id,
    name: nirvanaError.name,
    code: nirvanaError.code,
    message: publicErrorMessages.get(nirvanaError.code) ?? DEFAULT_ERROR_MESSAGE,
  };
}

export const NIRVANA_ERROR_NAME = 'GravityZoneNxError';
export const DEFAULT_ERROR_MESSAGE = 'Internal server error';

// TODO: check error codes and assign better public texts
const publicErrorMessages = new Map<number, string>([
  [NirvanaErrorCode.UNKNOWN, DEFAULT_ERROR_MESSAGE],
  [NirvanaErrorCode.STREAM_CLOSED, 'Placeholder:NirvanaErrorCode.STREAM_CLOSED'],
  [NirvanaErrorCode.STREAM_ALREADY_OPEN, 'Placeholder:NirvanaErrorCode.STREAM_ALREADY_OPEN'],
  [NirvanaErrorCode.STREAM_ID_INVALID, 'Placeholder:NirvanaErrorCode.STREAM_ID_INVALID'],
  [NirvanaErrorCode.STREAM_START_FAILED, 'Placeholder:NirvanaErrorCode.STREAM_START_FAILED'],
  [NirvanaErrorCode.STREAM_NOT_READY, 'Placeholder:NirvanaErrorCode.STREAM_NOT_READY'],
  [NirvanaErrorCode.CLIENT_RESPONSE_INVALID, 'Placeholder:NirvanaErrorCode.CLIENT_RESPONSE_INVALID'],
  [NirvanaErrorCode.KEEP_ALIVE_FAILED, 'Placeholder:NirvanaErrorCode.KEEP_ALIVE_FAILED'],
  [NirvanaErrorCode.GATEWAY_SHUTDOWN, 'Placeholder:NirvanaErrorCode.GATEWAY_SHUTDOWN'],
  [NirvanaErrorCode.MQ_ERROR, 'Internal communication error'],
  [NirvanaErrorCode.CLIENT_RESPONSE_ERROR, 'Placeholder:NirvanaErrorCode.CLIENT_RESPONSE_ERROR'],
  [NirvanaErrorCode.INVALID_MESSAGE_TYPE, 'Placeholder:NirvanaErrorCode.INVALID_MESSAGE_TYPE'],
  [NirvanaErrorCode.INIT_TIMEOUT, 'Placeholder:NirvanaErrorCode.INIT_TIMEOUT'],
  [NirvanaErrorCode.CLIENT_DISCONNECTED, 'Placeholder:NirvanaErrorCode.CLIENT_DISCONNECTED'],
  [NirvanaErrorCode.DUPLICATE_STREAM_ID, 'Placeholder:NirvanaErrorCode.DUPLICATE_STREAM_ID'],
  [NirvanaErrorCode.ACCESS_DENIED, 'Access denied'],
  [NirvanaErrorCode.SERVER_SHUTDOWN, 'Server shutdown'],
  [NirvanaErrorCode.UNEXPECTED_STOP, 'Placeholder:NirvanaErrorCode.UNEXPECTED_STOP'],
  [NirvanaErrorCode.INVALID_MESSAGE, 'Placeholder:NirvanaErrorCode.INVALID_MESSAGE'],
  [NirvanaErrorCode.INVALID_DATA, 'Invalid data'],
  [NirvanaErrorCode.STREAM_START_FAILED, 'Placeholder:NirvanaErrorCode.STREAM_START_FAILED'],
  [NirvanaErrorCode.USER_VALIDATION_ERROR, 'User validation error'],
  [NirvanaErrorCode.ENDPOINT_VALIDATION_ERROR, 'Endpoint validation error'],
  [NirvanaErrorCode.TWO_FACTOR_AUTHENTICATION_FAILED, '2FA code is not correct'],
  [NirvanaErrorCode.AUTHENTICATION_FAILED, 'Placeholder:NirvanaErrorCode.AUTHENTICATION_FAILED'],
  [NirvanaErrorCode.INVALID_SOCKET_ID, 'Placeholder:NirvanaErrorCode.INVALID_SOCKET_ID'],
  [NirvanaErrorCode.REQUEST_TIMED_OUT, 'Placeholder:NirvanaErrorCode.REQUEST_TIMED_OUT'],
  [
    NirvanaErrorCode.SAME_USER_SAME_ENDPOINT_LIMIT_REACHED,
    'Placeholder:NirvanaErrorCode.SAME_USER_SAME_ENDPOINT_LIMIT_REACHED',
  ],
  [
    NirvanaErrorCode.SAME_USER_MULTIPLE_ENDPOINTS_LIMIT_REACHED,
    'Placeholder:NirvanaErrorCode.SAME_USER_MULTIPLE_ENDPOINTS_LIMIT_REACHED',
  ],
  [
    NirvanaErrorCode.MULTIPLE_USERS_SAME_ENDPOINT_LIMIT_REACHED,
    'Placeholder:NirvanaErrorCode.MULTIPLE_USERS_SAME_ENDPOINT_LIMIT_REACHED',
  ],
  [NirvanaErrorCode.COMPANY_VALIDATION_ERROR, 'Company validation error'],
  [NirvanaErrorCode.FEATURE_CURRENTLY_NOT_AVAILABLE, 'Feature currently not available'],
]);
